import React, { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Filter as FilterIcon, RotateCcw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Drill } from '@/components/drill-library/types/drill-types';
import { TagPreference } from '@/components/drill-library/types/drill-types';
import FilterModal from '@/components/drill-library/FilterModal';
// Remove AI search/voice

interface DrillLibrarySubheaderProps {
    drills: Drill[];
    tagPreferences: { [key: string]: TagPreference };
    onFilterChange: (filters: { searchTerm: string; selectedPresets: string[] }) => void;
    leftColumns?: number;
}

const TAXONOMY_KEYS = [
    'Phase',
    'Biomechanical Intent',
    'Movement Pattern (Broad)',
    'Gait Phase Target',
    'Body postural / lateralisation',
    'Domain'
] as const;

const DrillLibrarySubheader: React.FC<DrillLibrarySubheaderProps> = ({
    drills,
    tagPreferences,
    onFilterChange,
    leftColumns = 3
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string }>(() => ({}));
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Simple search/filter: auto-apply on change
    useEffect(() => {
        // Compose a flat preset array from selected categorical filters to reuse onFilterChange contract
        const presets = Object.entries(selectedFilters)
            .filter(([_, v]) => v && v !== 'all')
            .map(([k, v]) => `${k}:${v}`);
        onFilterChange({ searchTerm, selectedPresets: presets });
    }, [searchTerm, selectedFilters]);

    // Responsive styling based on column width
    const isCompact = leftColumns <= 2;
    const isVeryCompact = leftColumns === 1;

    const setFilterValue = (key: string, value: string) => {
        setSelectedFilters(prev => {
            const next = { ...prev };
            if (value === 'all') delete next[key]; else next[key] = value;
            return next;
        });
    };

    const getMaxVisibleTags = () => {
        // More conservative approach - always start with fewer tags
        if (isVeryCompact) return 2;
        if (isCompact) return 3;
        return 4;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedFilters({});
        onFilterChange({ searchTerm: '', selectedPresets: [] });
    };
    const hasActiveFilters = searchTerm || Object.keys(selectedFilters).length > 0;

    const handleOpenFilters = () => setIsFilterModalOpen(true);
    const handleCloseFilters = () => setIsFilterModalOpen(false);

    const handleApplyFromModal = (filters: any) => {
        // Convert modal Set-based filters to preset list and reflect a primary selection per key
        const presets: string[] = [];
        const nextLocal: { [key: string]: string } = {};
        Object.entries(filters || {}).forEach(([key, value]) => {
            const arr = Array.from((value as Set<string>) || []);
            if (arr.length > 0) {
                nextLocal[key] = arr[0];
                arr.forEach(v => presets.push(`${key}:${v}`));
            }
        });
        setSelectedFilters(nextLocal);
        onFilterChange({ searchTerm, selectedPresets: presets });
        setIsFilterModalOpen(false);
    };

    return (
        <div className={`bg-muted/30 border-b border-border ${isVeryCompact ? 'p-2' : isCompact ? 'p-3' : 'p-4'} h-full flex flex-col`}>
            {/* Filter dropdown row */}
            <div className="flex items-center gap-2 flex-shrink-0">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 flex-1">
                    {TAXONOMY_KEYS.map(key => (
                        <Select key={key} value={selectedFilters[key] || 'all'} onValueChange={(v) => setFilterValue(key, v)}>
                            <SelectTrigger className={isVeryCompact ? 'h-8 text-xs' : isCompact ? 'h-9 text-xs' : 'h-10 text-sm'}>
                                <SelectValue placeholder={`All ${key}`} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All {key}</SelectItem>
                                {/* We don’t have taxonomy source here; infer from drills */}
                                {Array.from(new Set(drills.flatMap(d => {
                                    const raw = (d.tags as any)?.[key];
                                    const arr = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                                    return arr as string[];
                                }).filter(Boolean))).map(opt => (
                                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ))}
                </div>

                {/* Compact action buttons: open filters modal + reset */}
                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleOpenFilters}
                        variant="outline"
                        size="sm"
                        className={isVeryCompact ? 'h-8 px-2' : isCompact ? 'h-9 px-3' : 'h-10 px-3'}
                        title="View all filters"
                    >
                        <FilterIcon className="h-4 w-4 mr-1" />
                        <span className="hidden md:inline">Filters</span>
                    </Button>
                    <Button
                        onClick={clearFilters}
                        variant="outline"
                        size="sm"
                        disabled={!hasActiveFilters}
                        className={isVeryCompact ? 'h-8 px-2' : isCompact ? 'h-9 px-3' : 'h-10 px-3'}
                        title="Reset filters"
                    >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        <span className="hidden md:inline">Reset</span>
                    </Button>
                </div>
            </div>

            {/* Search/Filter - Inline Interface */}
            <div className="flex items-center gap-2 flex-shrink-0 mt-2">
                <div className="flex-1">
                    <Input
                        type="text"
                        placeholder={isVeryCompact ? "Search drills..." : isCompact ? "Search or filter..." : "Search by name, category, domain"}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`${isVeryCompact ? 'h-8 text-xs' : isCompact ? 'h-9 text-sm' : 'h-10 text-sm'} bg-background border-2 focus:border-primary/50 transition-colors`}
                    />
                </div>
            </div>

            {/* Full filters modal shared with Drill Library */}
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={handleCloseFilters}
                onApply={handleApplyFromModal}
                activeFilters={{}}
            />
        </div>
    );
};

export default DrillLibrarySubheader;
